import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import type { ImageFormats } from '@eneco/cms-types';
import { Image as SparkyImage, Stack, Text } from '@eneco/sparky';

import type { ImageContent } from './image-content.types';

/**
 * The 16:9 crops of Sitecore's `Image16by9` rendering. Sitecore sends the image already cut; on Sanity
 * the formats the CMS gives the block's aspect ratio win (`image-formats.ts`), so these apply only to a
 * block without one.
 */
const IMAGE_FORMATS: ImageFormats = {
  default: { width: 768, height: 432 },
  small: { width: 768, height: 432 },
  medium: { width: 1024, height: 576 },
  large: { width: 1024, height: 576 },
  extraLarge: { width: 1024, height: 576 },
};

/**
 * `Image` block for the CMS-agnostic lane, serving **both** providers. Content (`image`, `captionText`)
 * comes from `useContent`; the provider image is resolved through `adapter.toImage`, so the
 * FocalPointImage-vs-Sanity-asset divergence lives in the adapter, not here.
 *
 * `maxHeight`/`objectFit` go through `useSetting` rather than `content`: Sitecore delivers them as
 * rendering *parameters* (`params`) while the CMS schema compiles them into ordinary block
 * fields. Reading only `content` — as this component used to — left both undefined on the Sitecore
 * lane, so the same image silently rendered at a different size per provider.
 *
 * The Sitecore Experience-Editor `editable`/`wrap()` branch is intentionally omitted (no meaning here).
 */

const Image: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ImageContent>();
  const image = adapter.toImage(content.image, IMAGE_FORMATS);

  const maxHeight = useSetting('maxHeight');
  // The schema types objectFit as a loose string; narrow to the Sparky variant.
  const objectFit = useSetting('objectFit') === 'cover' ? 'cover' : 'contain';

  if (!image) return null;

  return (
    <Stack gap="6">
      <SparkyImage
        width="100%"
        maxHeight={maxHeight ? `${maxHeight}px` : undefined}
        objectFit={objectFit}
        src={image.src}
        sources={image.sources}
        aspectRatio={image.aspectRatio}
        alt={image.alt ?? content.captionText ?? ''}
      />
      {content.captionText ? (
        <Text size={{ md: 'BodyL' }} color="textPrimary">
          {content.captionText}
        </Text>
      ) : null}
    </Stack>
  );
};

export default Image;
